/**
 * O suma de bani, cu toate distinctiile pe care Excel le afiseaza identic si noi le separam:
 *
 *   1. suma negativa      — minus SI culoare; la print minus SI paranteze;
 *   2. linie de credit    — facilitate, nu deficit: `--ink-2`, badge `linie`, scadenta inline;
 *   3. celula goala       — punct central de tip hairline (fisierul nu poate distinge
 *                           „nicio plata planificata" de „planificat zero", noi trebuie);
 *   4. `0,00`             — `--ink-3`, ca zeroul planificat sa nu concureze cu sumele reale.
 *
 * Zecimalele se afiseaza la 95% in `--ink-2`: lizibilitatea formatului `#,##0` din Excel fara a
 * ascunde banii. Cifrele sunt mereu tabulare (`.num`), altfel coloanele danseaza la scroll.
 */

import { formatIsoDate, formatMinor, splitMinor } from '@evomag/core/format';

export interface PropsMoney {
  /** Suma in unitati minore. `null`/`undefined` = nicio valoare (nu zero). */
  minor: number | null | undefined;
  /** Sufix estompat, separat de cifre ca alinierea sa nu se strice. Implicit `lei`. */
  moneda?: string | null;
  /** `2` implicit; `0` pentru axe si totaluri mari. */
  zecimale?: 0 | 2;
  /** Cate unitati minore are o unitate majora (100 peste tot in aplicatie). */
  scalaMinora?: number;
  /** Paranteze pentru print/contabilitate, pe langa minus. */
  accounting?: boolean;
  /**
   * Linie de credit (BCR-LC, Unicredit-LC): valoare in `--ink-2` cu badge `linie`, NICIODATA
   * rosu. A vopsi o facilitate in rosu tipa ca e o problema acolo unde nu e.
   */
  creditLine?: boolean;
  /** Scadenta facilitatii, ISO; afisata langa badge-ul `linie`. */
  scadenta?: string | null;
  /** Delta: pozitivul primeste `+` si `--money-pos-delta`. */
  delta?: boolean;
  className?: string;
  /** Nume accesibil suplimentar (ex. „De plata, Marti 18.08.2026"). */
  ariaLabel?: string;
}

export function Money({
  minor,
  moneda = 'lei',
  zecimale = 2,
  scalaMinora = 100,
  accounting = false,
  creditLine = false,
  scadenta = null,
  delta = false,
  className,
  ariaLabel,
}: PropsMoney) {
  // --- 3. celula goala -----------------------------------------------------------------------
  if (minor === null || minor === undefined) {
    return (
      <span
        className={`num block text-center text-ink-3 ${className ?? ''}`}
        title="Nicio plată planificată"
        aria-label={ariaLabel ?? 'Nicio plată planificată'}
      >
        <span aria-hidden="true">·</span>
      </span>
    );
  }

  const negativ = minor < 0;
  const zero = minor === 0;

  let culoare = 'text-ink-1';
  if (creditLine) culoare = 'text-ink-2';
  else if (negativ) culoare = 'text-neg';
  else if (zero) culoare = 'text-ink-3';
  else if (delta) culoare = 'text-pos';

  // Zecimalele se estompeaza in `--ink-2` doar cnd cifrele sunt neutre; pe o suma negativa
  // ar rupe rosul in doua, deci acolo estompam prin opacitate, pastrand nuanta.
  const clasaZecimale = negativ || delta ? 'opacity-80' : 'text-ink-2';

  const bucati = zecimale === 2 ? splitMinor(minor, scalaMinora) : null;
  // Semnul se randeaza separat, deci partea intreaga se formateaza pe valoarea absoluta —
  // altfel la `zecimale={0}` ar aparea doua minusuri.
  const intreg = bucati
    ? bucati.integer
    : formatMinor(Math.abs(minor), { decimals: 0, minorScale: scalaMinora });
  const semn = bucati ? bucati.sign : negativ ? '-' : '';
  const prefixDelta = delta && minor > 0 ? '+' : '';

  return (
    <span
      className={`num inline-flex items-baseline gap-1 whitespace-nowrap ${culoare} ${className ?? ''}`}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <span>
        {accounting && negativ ? '(' : ''}
        {prefixDelta}
        {semn}
        {intreg}
        {bucati ? (
          <span className={`text-[95%] ${clasaZecimale}`}>
            {bucati.separator}
            {bucati.fraction}
          </span>
        ) : null}
        {accounting && negativ ? ')' : ''}
      </span>

      {moneda ? (
        <span className="text-[0.85em] font-normal text-ink-3" aria-hidden="true">
          {moneda}
        </span>
      ) : null}

      {creditLine ? (
        <span className="badge" title="Linie de credit — facilitate, nu deficit">
          linie
          {scadenta ? <span className="text-ink-3">scad. {formatIsoDate(scadenta)}</span> : null}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Sold creditor pe furnizor (plata in avans). Al treilea lucru pe care Excel il afisa identic
 * cu un negativ: nu e datorie, e bani dati inainte.
 */
export function ChipAvans({ minor, moneda = 'lei' }: { minor: number; moneda?: string }) {
  return (
    <span className="chip" title="Furnizor plătit în avans (sold creditor)">
      plătit în avans
      <Money minor={Math.abs(minor)} moneda={moneda} className="text-ink-2" />
    </span>
  );
}
